import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'

// P24 (Tudor, 2026-10-24, the canvas verdict A): the CP's one day / hour picker, in place of the browser's date, time and
// datetime-local fields — a calendar with the list of hours (react-datepicker, its CSS already loaded), the value written out in the
// field. A drop-in: the value stays the native field's string ('YYYY-MM-DD', 'HH:mm', 'YYYY-MM-DDTHH:mm'; '' when empty) and the
// handlers receive an event-like { target: { name, id, value } }, so Formik and `event.target.value` callers keep working. Wall time
// only: no value is moved between time zones here. A day or an hour can also be typed (7.11.2026 9:30, 2026-11-07T09:30, 7:15).
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad = number => String(number).padStart(2, '0')
const VALUE = { date: /^(\d{4})-(\d{2})-(\d{2})$/, datetime: /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/, time: /^(\d{2}):(\d{2})$/ }
// a value's parts, or null when it is not a real day / hour
const partsOf = (value, mode) => {
  const match = VALUE[mode].exec(value || '')
  if (!match) return null
  const numbers = match.slice(1).map(Number)
  const [year, month, day, hour, minute] = mode === 'time' ? [2000, 1, 1, ...numbers] : mode === 'date' ? [...numbers, 0, 0] : numbers
  const check = new Date(Date.UTC(year, month - 1, day))
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day || hour > 23 || minute > 59) return null
  return { year, month, day, hour, minute }
}
const valueOf = ({ year, month, day, hour, minute }, mode) => mode === 'time' ? pad(hour) + ':' + pad(minute)
  : `${year}-${pad(month)}-${pad(day)}` + (mode === 'datetime' ? `T${pad(hour)}:${pad(minute)}` : '')
// „Sat 7 Nov 2026 · 09:00”, „Mon 2 Nov 2026”, „07:15”
export const formatValue = (value, mode) => {
  const p = partsOf(value, mode)
  if (!p) return value || ''
  const day = `${DAYS[new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay()]} ${p.day} ${MONTHS[p.month - 1]} ${p.year}`
  return mode === 'time' ? valueOf(p, 'time') : mode === 'date' ? day : day + ' · ' + valueOf(p, 'time')
}
// The typed text as the native string: '' for an empty field, null for a text that is not (yet) a real day / hour.
export const parseText = (text, mode) => {
  const typed = String(text || '').trim()
  if (!typed) return ''
  if (partsOf(typed, mode)) return typed
  const clock = mode === 'date' ? '' : '\\s*[·,\\s]\\s*(\\d{1,2})[:.](\\d{2})'
  const time = /^(\d{1,2})[:.](\d{2})$/.exec(typed)
  if (mode === 'time') return time ? checked({ year: 2000, month: 1, day: 1, hour: +time[1], minute: +time[2] }, mode) : null
  const numeric = new RegExp('^(\\d{1,2})[./](\\d{1,2})[./](\\d{4})' + clock + '$').exec(typed)
  if (numeric) return checked({ day: +numeric[1], month: +numeric[2], year: +numeric[3], hour: +(numeric[4] || 0), minute: +(numeric[5] || 0) }, mode)
  const written = new RegExp('^(?:[A-Za-z]{3},?\\s+)?(\\d{1,2})\\s+([A-Za-z]{3})[a-z]*\\.?\\s+(\\d{4})' + clock + '$').exec(typed)
  const month = written && MONTHS.findIndex(name => name.toLowerCase() === written[2].toLowerCase()) + 1
  if (month) return checked({ day: +written[1], month, year: +written[3], hour: +(written[4] || 0), minute: +(written[5] || 0) }, mode)
  return null
}
// the picker's mode for a native input type, for the form helpers that take one ('number', 'email'… stay inputs)
export const dateFieldMode = type => ({ date: 'date', time: 'time', 'datetime-local': 'datetime' })[type]
const checked = (parts, mode) => { const value = valueOf(parts, mode); return partsOf(value, mode) ? value : null }
// the picker reads a local Date with the same wall day and hour
const toPicker = (value, mode) => { const p = partsOf(value, mode); return p ? new Date(p.year, p.month - 1, p.day, p.hour, p.minute) : null }
const fromPicker = (date, mode) => valueOf({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hour: date.getHours(), minute: date.getMinutes() }, mode)
// the list's next hour from now: a day picked before any hour starts there, not at midnight
const nextListed = step => {
  const now = new Date(), minutes = Math.min(Math.ceil((now.getHours() * 60 + now.getMinutes()) / step) * step, 1440 - step)
  return pad(Math.floor(minutes / 60)) + ':' + pad(minutes % 60)
}
const dayOf = value => (value || '').slice(0, 10)
const timeOf = value => (value || '').slice(11, 16)
const PLACEHOLDER = { date: 'Choose a day', time: 'Choose a time', datetime: 'Choose a day and time' }
const ICONS = {
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>,
  clock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
}

// A form helper's <input>: the picker for a date / time type, the plain input for any other.
export const InputOrPicker = ({ type, ...props }) => dateFieldMode(type) ? <DateTimeField {...props} mode={dateFieldMode(type)} /> : <input {...props} type={type} />

// `mode` 'date' | 'time' | 'datetime'; `min` / `max` as the native attributes; `step` the minutes between listed hours (every 15 by default, so a
// competition can start at 10:15: Tudor, 2026-10-29; a value off the list stays listed); `zoneLabel` the
// row under the calendar (the time zone); a From / To pair draws its range: the From field gets `rangeEnd` (the To value), the To field
// `rangeStart` (the From value). `invalid` and `bsSize` as reactstrap's Input. Every other attribute (aria-*, title…) goes to the field.
export default function DateTimeField({ mode = 'date', id, name, value = '', onChange, onBlur, min, max, disabled, required, className = 'form-control',
  invalid, bsSize, placeholder, step = 15, zoneLabel, rangeStart, rangeEnd, type: _native, ...rest }) {
  const [open, setOpen] = useState(false)
  // the text while it is typed; null = the value written out
  const [typed, setTyped] = useState(null)
  const [alignRight, setAlignRight] = useState(false)
  const box = useRef(null)
  const popup = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const away = event => { if (!box.current?.contains(event.target)) setOpen(false) }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])
  // a field near the right edge opens its calendar to the left
  useLayoutEffect(() => {
    if (!open || !popup.current || typeof window === 'undefined') return
    const { right } = popup.current.getBoundingClientRect()
    setAlignRight(right > (window.innerWidth || document.documentElement.clientWidth) - 8)
  }, [open])
  const send = (handler, next) => {
    const target = { name, id, value: next, type: 'text', outerHTML: '' }
    handler?.({ target, currentTarget: target, persist() {} })
  }
  const change = next => { if (next !== value) send(onChange, next) }
  const selected = toPicker(value, mode)
  const pick = date => {
    if (!date) return
    setTyped(null)
    if (mode !== 'datetime') { change(fromPicker(date, mode)); setOpen(false); return }
    // a day keeps the hour and waits for it; an hour closes the calendar
    const day = fromPicker(date, 'date')
    if (day !== dayOf(value)) { change(day + 'T' + (timeOf(value) || nextListed(step))); return }
    change(fromPicker(date, 'datetime'))
    setOpen(false)
  }
  const day = mode !== 'time'
  const classes = [className, invalid && 'is-invalid', bsSize && 'form-control-' + bsSize].filter(Boolean).join(' ')
  const offStep = selected && (selected.getMinutes() % step !== 0)
  return <div ref={box} className="date-time-field">
    <input {...rest} id={id} name={name} type="text" autoComplete="off" className={classes} value={typed ?? formatValue(value, mode)}
      placeholder={placeholder ?? PLACEHOLDER[mode]} disabled={disabled} required={required} min={min} max={max} aria-haspopup="dialog" aria-expanded={open}
      // a click or the down arrow opens the calendar; a focus alone (Tab, a form showing its first error) does not
      onClick={() => { if (!disabled) setOpen(true) }}
      onChange={event => { const text = event.target.value; setTyped(text); const next = parseText(text, mode); if (next !== null) change(next) }}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' && !open && !disabled) { event.preventDefault(); setOpen(true) }
        if (event.key === 'Escape') setOpen(false)
        if (event.key === 'Enter' && open) { event.preventDefault(); setOpen(false); setTyped(null) }
        if (event.key === 'Tab') setOpen(false)
      }}
      onBlur={() => { setTyped(null); send(onBlur, value) }} />
    <span className="date-time-field-icon" aria-hidden="true">{day ? ICONS.calendar : ICONS.clock}</span>
    {/* A click in the calendar is not a click on a <label> around the field, which would open it again. */}
    {open && !disabled && <div ref={popup} className={'date-time-field-popup' + (alignRight ? ' align-right' : '')} onClick={event => event.preventDefault()}>
      <DatePicker inline calendarStartDay={1} selected={selected} onChange={pick} minDate={day ? toPicker(dayOf(min), 'date') : null}
        maxDate={day ? toPicker(dayOf(max), 'date') : null} showTimeSelect={mode !== 'date'} showTimeSelectOnly={mode === 'time'} timeIntervals={step}
        timeCaption="Time" timeFormat="HH:mm" injectTimes={offStep ? [selected] : undefined}
        {...(rangeEnd !== undefined ? { selectsStart: true, startDate: selected, endDate: toPicker(rangeEnd, 'date') } : {})}
        {...(rangeStart !== undefined ? { selectsEnd: true, startDate: toPicker(rangeStart, 'date'), endDate: selected } : {})}>
        {zoneLabel && <div className="date-time-field-zone">{zoneLabel}</div>}
      </DatePicker>
    </div>}
  </div>
}
